import { generateTestId } from "@client-shared";
import { COUNTRIES_WITH_LOCALES, REGIONS_WITH_LOCALES, normalizeVendorCountryCode } from "@client-shared/lib/constants";
import type { IntensityComparisonCoffee } from "@bcc/components/ui/intensity-scale";
import type { VendorRegion } from "@shared/services/pim/types";
import { isIntensityPosition, type IntensityPosition } from "@shared/types/intensity";
import type { LocaleObject, RegionLocaleObject } from "@shared/types";

export const setProductsViewTestId = (identifier: string): string => generateTestId("ProductsView", identifier);

const isKnownVendorRegion = (region: string): region is keyof typeof REGIONS_WITH_LOCALES => Object.hasOwn(REGIONS_WITH_LOCALES, region);

interface VendorLocaleMetadataInput {
  region: string;
  countryCode?: string | null;
}

interface VendorLocaleMetadata {
  region: VendorRegion;
  locales: LocaleObject[];
}

export const getVendorLocaleMetadata = ({ region, countryCode }: VendorLocaleMetadataInput): VendorLocaleMetadata => {
  if (!isKnownVendorRegion(region)) {
    throw new Error(`Missing region locale mapping for ${region}`);
  }

  const normalizedCountryCode = normalizeVendorCountryCode(countryCode);
  if (normalizedCountryCode) {
    return { region, locales: COUNTRIES_WITH_LOCALES[normalizedCountryCode] };
  }

  return { region, locales: REGIONS_WITH_LOCALES[region] };
};

export const getRegionLocales = (
  region: string
): {
  region: keyof typeof REGIONS_WITH_LOCALES;
  locales: RegionLocaleObject[];
} => {
  const result = getVendorLocaleMetadata({ region });
  return { region: result.region, locales: REGIONS_WITH_LOCALES[result.region] };
};

/**
 * The only fields the builder reads. Narrower than `Product` so both `Product`
 * and `FormProduct` (whose `certifications` differs) can be passed without a
 * cast.
 */
interface ScoredProduct {
  id?: string;
  serviceId?: string;
  localId?: string;
  title?: string;
  localTitle?: string;
  // A form-sourced coffee stores "unscored" as null; those are dropped like absent ones.
  intensityPosition?: IntensityPosition | null;
}

/**
 * Every value a coffee can be recognised by, lower-cased.
 *
 * Titles alone are not a safe identity — the same coffee routinely differs in
 * casing between PIM and Salesforce, and PIM copy can be copy-edited or
 * localised after import — but they are the only link between two flat lists
 * that share no key. So match on ids *and* the normalised title, and treat any
 * overlap as the same coffee. That mirrors how the rest of the app already
 * treats titles (the duplicate-name check normalises with
 * `trim().toLowerCase()`), and the app forbids two coffees sharing a title.
 */
export const intensityMatchTokens = (product: ScoredProduct): string[] =>
  [product.id, product.serviceId, product.localId, product.title?.trim().toLowerCase(), product.localTitle?.trim().toLowerCase()]
    .filter((token): token is string => Boolean(token))
    .map((token) => token.toLowerCase());

/**
 * Builds the comparison markers for the intensity scale from the roaster's
 * existing line-up.
 *
 * The PIM and Salesforce lists are the approved and provisional sides of the
 * same score (PIM `WEB_INTENSITY_*` is written only after the beans manager
 * signs a score off), so which list a coffee came from *is* its confidence.
 * PIM wins where a coffee appears in both, and unscored coffees are dropped —
 * a marker with no position has nowhere to sit on the scale.
 */
export const buildIntensityComparisonCoffees = (
  pimProducts: readonly ScoredProduct[] = [],
  salesforceProducts: readonly ScoredProduct[] = []
): IntensityComparisonCoffee[] => {
  const coffees: IntensityComparisonCoffee[] = [];
  const claimed = new Set<string>();

  const collect = (products: readonly ScoredProduct[], confidence: IntensityComparisonCoffee["confidence"]) => {
    for (const product of products) {
      const name = product.title?.trim();
      if (!name || !isIntensityPosition(product.intensityPosition)) continue;

      const matchTokens = intensityMatchTokens(product);
      // PIM is collected first, so an approved score is never downgraded by a
      // stale provisional one for the same coffee — however differently that
      // coffee is spelled or keyed on the Salesforce side.
      if (matchTokens.some((token) => claimed.has(token))) continue;
      matchTokens.forEach((token) => claimed.add(token));

      coffees.push({
        id: product.id ?? product.serviceId ?? name.toLowerCase(),
        name,
        position: product.intensityPosition,
        confidence,
        matchTokens,
      });
    }
  };

  collect(pimProducts, "approved");
  collect(salesforceProducts, "provisional");

  return coffees.sort((left, right) => left.position - right.position);
};
